import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Card, ScrollableWindow, ColumnTitle, PaginationContainer, PaginationControls, PageIndicator, PageDotsRow, PageDot, NavButtonWithTooltip } from './StyledComponents';
import TooltipWrapper from './TooltipWrapper';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

interface UseCaseDisplayProps {
  useCase: string;
  currentPage?: number;
  totalPages?: number;
  onPrev?: () => void;
  onNext?: () => void;
  onNavigateToPage?: (page: number) => void;
  isNavigationLocked?: boolean;
  canGoBackToPrev?: boolean;
  tooltipText?: string;
  pageStatuses?: ('unstarted' | 'partial' | 'done')[];
}

const extractUseCaseName = (useCaseText: string): string | null => {
  // Find "Use Case Name:" and get the next non-empty line
  const lines = useCaseText.split('\n');
  const nameIndex = lines.findIndex(line => line.trim().toLowerCase().includes('use case name:'));
  
  if (nameIndex !== -1) {
    // Look for the next non-empty line after "Use Case Name:"
    for (let i = nameIndex + 1; i < lines.length; i++) {
      const trimmedLine = lines[i].trim();
      if (trimmedLine && !trimmedLine.toLowerCase().startsWith('id:')) {
        return trimmedLine;
      }
    }
  }
  
  return null;
};

const UseCaseDisplay: React.FC<UseCaseDisplayProps> = ({
  useCase, currentPage, totalPages, onPrev, onNext, onNavigateToPage,
  isNavigationLocked = false, canGoBackToPrev = false, tooltipText, pageStatuses,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const useCaseName = useMemo(() => extractUseCaseName(useCase), [useCase]);

  const canGoPrev = (currentPage !== undefined && currentPage > 0) || canGoBackToPrev;
  const canGoNext = currentPage !== undefined && totalPages !== undefined && currentPage < totalPages - 1;
  const isPrevLocked = isNavigationLocked && !canGoBackToPrev;
  const isNextLocked = isNavigationLocked && canGoNext;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              margin: '0',
              color: '#34495e',
              transition: 'transform 0.3s ease',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1',
              minWidth: '24px',
              minHeight: '24px',
            }}
            aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
          >
            ❯
          </button>
          <ColumnTitle style={{ marginBottom: 0, marginTop: 0 }}>
            {useCaseName || 'Aktueller Use Case'}
          </ColumnTitle>
        </div>
        {totalPages !== undefined && totalPages > 0 && (
          <PaginationContainer>
            <PaginationControls>
              <TooltipWrapper
                tooltipText={isPrevLocked ? tooltipText : undefined}
                showTooltip={isPrevLocked}
              >
                <NavButtonWithTooltip
                  onClick={isPrevLocked ? undefined : onPrev}
                  disabled={!canGoPrev}
                  isDisabled={isPrevLocked}
                >
                  <FaArrowLeft />
                </NavButtonWithTooltip>
              </TooltipWrapper>
              <PageIndicator>
                Use Case {currentPage !== undefined ? currentPage + 1 : 1} von {totalPages}
              </PageIndicator>
              <TooltipWrapper
                tooltipText={isNextLocked ? tooltipText : undefined}
                showTooltip={isNextLocked}
              >
                <NavButtonWithTooltip
                  onClick={isNextLocked ? undefined : onNext}
                  disabled={!canGoNext}
                  isDisabled={isNextLocked}
                >
                  <FaArrowRight />
                </NavButtonWithTooltip>
              </TooltipWrapper>
            </PaginationControls>
            {pageStatuses && pageStatuses.length > 1 && (
              <PageDotsRow>
                {pageStatuses.map((status, i) => (
                  <PageDot
                    key={i}
                    $status={status}
                    $active={i === currentPage}
                    disabled={status === 'unstarted' || isNavigationLocked && status !== 'done' && status !== 'partial'}
                    title={status === 'done' ? `Use Case ${i + 1} – abgeschlossen` : status === 'partial' ? `Use Case ${i + 1} – in Bearbeitung` : `Use Case ${i + 1} – noch nicht bearbeitet`}
                    onClick={() => {
                      if (status !== 'unstarted' && onNavigateToPage) onNavigateToPage(i);
                    }}
                  />
                ))}
              </PageDotsRow>
            )}
          </PaginationContainer>
        )}
      </div>
      {isExpanded && (
        <ScrollableWindow style={{ marginTop: '1rem' }}>
          <ReactMarkdown>{useCase}</ReactMarkdown>
        </ScrollableWindow>
      )}
    </Card>
  );
};

export default UseCaseDisplay;
